<script lang="ts">
    import { link } from 'svelte-spa-router';
    import CalendarIcon from '../../icons/CalendarIcon.svelte';
    import TagIcon from '../../icons/TagIcon.svelte';
    import CkEditorContent from '../CkEditorContent.svelte';
    import BillPositionPill from './BillPositionPill.svelte';
    import BillSponsorItem from './BillSponsorItem.svelte';
    import {
        billDetailRoute,
        getOfficialsWithProfiles,
        parseSessionDisplayName,
        type BillDetail,
        type BillPosition,
        type BillSponsor
    } from '../../api/bills';
    import * as t from '../../translations/Bills.ts';

    export let detail: BillDetail;
    const FULL_TITLE_PREVIEW_LENGTH = 300;
    const ACTIONS_PREVIEW_COUNT = 5;
    const RELATED_PREVIEW_COUNT = 5;
    const headingClass = 'mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500';
    const linkClass =
        '!text-[#1b75d0] !no-underline hover:!underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#2563eb)] focus-visible:ring-offset-2 rounded-sm';

    let fullTitleExpanded = false;
    let cosponsorsExpanded = false;
    let actionsExpanded = false;
    let actionsSection: HTMLElement;
    let relatedExpanded = false;
    let relatedSection: HTMLElement;
    let noProfileIds = new Set<number>();
    let requestedProfileIds = new Set<number>();
    let profileLookupDetail: BillDetail | null = null;

    $: session = parseSessionDisplayName(detail.sessionDisplayName);
    $: spotlight = detail.spotlight;
    $: identifierPrefix = `${detail.identifierDisplay}:`;
    $: titleWithoutIdentifier = detail.title.startsWith(identifierPrefix)
        ? detail.title.slice(identifierPrefix.length).trim()
        : null;
    $: fullTitleIsLong = detail.fullTitle.length > FULL_TITLE_PREVIEW_LENGTH;
    $: fullTitleText =
        fullTitleIsLong && !fullTitleExpanded
            ? `${detail.fullTitle.slice(0, FULL_TITLE_PREVIEW_LENGTH)}…`
            : detail.fullTitle;
    $: cosponsorsId = `bill-cosponsors-${detail.identifier.replace(/\W+/g, '-')}`;
    $: actionsId = `bill-actions-${detail.identifier.replace(/\W+/g, '-')}`;
    $: hiddenActionCount = Math.max(0, detail.actions.length - ACTIONS_PREVIEW_COUNT);
    $: visibleActions = actionsExpanded ? detail.actions : detail.actions.slice(0, ACTIONS_PREVIEW_COUNT);
    $: relatedId = `bill-related-${detail.identifier.replace(/\W+/g, '-')}`;
    $: hiddenRelatedCount = Math.max(0, detail.relatedProposals.length - RELATED_PREVIEW_COUNT);
    $: visibleRelated = relatedExpanded ? detail.relatedProposals : detail.relatedProposals.slice(0, RELATED_PREVIEW_COUNT);
    $: detail, resetToggles();
    $: checkProfiles(detail.primarySponsors);
    $: if (cosponsorsExpanded) checkProfiles(detail.otherSponsors);

    function resetToggles() {
        fullTitleExpanded = false;
        cosponsorsExpanded = false;
        actionsExpanded = false;
        relatedExpanded = false;
    }

    /** Links stay on until the lookup says otherwise, so a failed lookup never hides working profiles. */
    function checkProfiles(sponsors: BillSponsor[]) {
        if (profileLookupDetail !== detail) {
            profileLookupDetail = detail;
            requestedProfileIds = new Set();
            noProfileIds = new Set();
        }
        const ids = sponsors.map((s) => s.officeOccupancyId).filter((id) => id > 0 && !requestedProfileIds.has(id));
        if (ids.length === 0) return;
        ids.forEach((id) => requestedProfileIds.add(id));

        const forDetail = detail;
        getOfficialsWithProfiles(ids)
            .then((found) => {
                if (forDetail !== detail) return;
                noProfileIds = new Set([...noProfileIds, ...ids.filter((id) => !found.has(id))]);
            })
            .catch((e) => console.warn('[Bills] Sponsor profile lookup failed:', e));
    }

    function collapse(section: HTMLElement) {
        if (section.getBoundingClientRect().top < 0) section.scrollIntoView({ block: 'start' });
    }

    function toggleActions() {
        actionsExpanded = !actionsExpanded;
        if (!actionsExpanded) collapse(actionsSection);
    }

    function toggleRelated() {
        relatedExpanded = !relatedExpanded;
        if (!relatedExpanded) collapse(relatedSection);
    }

    function positionStatement(position: BillPosition): string {
        if (position === 'support') return t.Position_Statement_Support();
        if (position === 'oppose') return t.Position_Statement_Oppose();
        return t.Position_Statement_Neutral();
    }

    function urlLabel(url: string): string {
        try {
            return new URL(url).hostname.replace(/^www\./, '');
        } catch {
            return url;
        }
    }

    /** Association-authored links leave the Action Center in a new tab (legacy defaultLinksToNewWindow). */
    function externalLinks(node: HTMLElement) {
        const apply = () => {
            for (const a of node.querySelectorAll('a[href]')) {
                a.setAttribute('target', '_blank');
                a.setAttribute('rel', 'noopener noreferrer');
            }
        };
        apply();
        const observer = new MutationObserver(apply);
        observer.observe(node, { childList: true, subtree: true });
        return { destroy: () => observer.disconnect() };
    }
</script>

<article class="bill-detail-panel" data-testid="bill-detail-panel">
    <header class="border-b border-gray-200 px-6 py-5">
        <div class="mb-2 flex flex-wrap items-center gap-2">
            <span class="text-sm font-semibold text-gray-900" aria-hidden={titleWithoutIdentifier ? 'true' : undefined} data-testid="bill-detail-identifier">{detail.identifierDisplay}</span>
            {#if spotlight}
                <BillPositionPill position={spotlight.position} />
            {/if}
        </div>
        <h1 class="text-xl font-bold leading-snug text-gray-900 md:text-2xl" data-testid="bill-detail-title">
            {#if titleWithoutIdentifier}<span class="sr-only">{identifierPrefix} </span>{titleWithoutIdentifier}{:else}{detail.title}{/if}
        </h1>
        <dl class="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500">
            {#if session.congressLabel}
                <div class="flex items-center gap-1.5">
                    <dt class="sr-only">{t.Detail_Session()}</dt>
                    <CalendarIcon class="shrink-0 text-gray-400" aria-hidden="true" />
                    <dd>{session.congressLabel}{session.yearRange ? ` (${session.yearRange})` : ''}</dd>
                </div>
            {/if}
            {#if spotlight?.categoryName}
                <div class="flex items-center gap-1.5">
                    <dt class="sr-only">{t.Detail_Category_Label()}</dt>
                    <TagIcon class="shrink-0 text-gray-400" aria-hidden="true" />
                    <dd>{spotlight.categoryName}</dd>
                </div>
            {/if}
        </dl>
    </header>

    <div class="px-6 py-5">
        {#if spotlight}
            <section class="mb-8" data-testid="bill-detail-position">
                <h2 class={headingClass}>{t.Detail_Our_Position()}</h2>
                <div class="rounded-xl border border-gray-200 bg-white p-4">
                    <p class="m-0 text-sm font-semibold text-gray-900" data-testid="bill-detail-position-statement">{positionStatement(spotlight.position)}</p>
                    {#if spotlight.explanation}
                        <div class="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-700" use:externalLinks>
                            <CkEditorContent
                                html={spotlight.explanation}
                                className="[&_a]:!text-[#1b75d0]"
                                testId="bill-detail-explanation"
                            />
                        </div>
                    {/if}
                </div>
            </section>
        {/if}

        {#if detail.fullTitle}
            <section class="mb-8">
                <h2 class={headingClass}>{t.Detail_Summary()}</h2>
                <p class="whitespace-pre-line text-sm leading-relaxed text-gray-700" data-testid="bill-detail-full-title">{fullTitleText}</p>
                {#if fullTitleIsLong}
                    <button
                        type="button"
                        class="mt-2 !border-0 !bg-transparent text-sm font-medium {linkClass}"
                        aria-expanded={fullTitleExpanded}
                        on:click={() => (fullTitleExpanded = !fullTitleExpanded)}
                    >
                        {fullTitleExpanded ? t.Show_Less() : t.Show_More()}
                    </button>
                {/if}
            </section>
        {/if}

        {#if detail.primarySponsors.length > 0 || detail.otherSponsors.length > 0}
            <section class="mb-8" data-testid="bill-detail-sponsors">
                <h2 class={headingClass}>{t.Detail_Sponsors()}</h2>
                {#if detail.primarySponsors.length > 0}
                    <ul class="m-0 list-none space-y-3 p-0">
                        {#each detail.primarySponsors as sponsor}
                            <li><BillSponsorItem {sponsor} showPhoto linkable={!noProfileIds.has(sponsor.officeOccupancyId)} /></li>
                        {/each}
                    </ul>
                {/if}
                {#if detail.otherSponsors.length > 0}
                    <div class="mt-4 border-t border-gray-100 pt-4">
                        <button
                            type="button"
                            class="inline-flex items-center gap-2 !border-0 !bg-transparent text-sm font-medium {linkClass}"
                            aria-expanded={cosponsorsExpanded}
                            aria-controls={cosponsorsId}
                            on:click={() => (cosponsorsExpanded = !cosponsorsExpanded)}
                            data-testid="bill-detail-cosponsors-toggle"
                        >
                            {t.Detail_Cosponsors(String(detail.otherSponsors.length))}
                            <svg class="h-4 w-4 transition-transform {cosponsorsExpanded ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {#if cosponsorsExpanded}
                            <ul id={cosponsorsId} class="m-0 mt-3 grid list-none p-0 gap-2 sm:grid-cols-2">
                                {#each detail.otherSponsors as sponsor}
                                    <li><BillSponsorItem {sponsor} linkable={!noProfileIds.has(sponsor.officeOccupancyId)} /></li>
                                {/each}
                            </ul>
                        {/if}
                    </div>
                {/if}
            </section>
        {/if}

        {#if detail.actions.length > 0}
            <section class="mb-8 scroll-mt-4" bind:this={actionsSection} data-testid="bill-detail-actions">
                <h2 class={headingClass}>{t.Detail_Legislative_Actions()}</h2>
                <ol id={actionsId} class="m-0 list-none space-y-0 p-0">
                    {#each visibleActions as action, i (i)}
                        <li class="flex gap-3" data-testid="bill-detail-action">
                            <div class="flex flex-col items-center pt-1">
                                <span
                                    class="h-3 w-3 shrink-0 rounded-full ring-2 ring-white {i === 0 ? 'bg-gray-900' : 'bg-gray-300'}"
                                    aria-hidden="true"
                                ></span>
                                {#if i < visibleActions.length - 1}
                                    <div class="mt-1 w-0.5 min-h-[1.75rem] flex-1 bg-gray-200" aria-hidden="true"></div>
                                {:else if hiddenActionCount > 0 && !actionsExpanded}
                                    <div class="mt-1 w-0.5 min-h-[1.25rem] flex-1 bg-gradient-to-b from-gray-200 to-transparent" aria-hidden="true"></div>
                                {/if}
                            </div>
                            <div class="min-w-0 flex-1 {i < visibleActions.length - 1 ? 'pb-6' : ''}">
                                {#if action.date}
                                    <p class="text-xs font-medium text-gray-500">{action.date}</p>
                                {/if}
                                <p class="mt-0.5 text-sm {i === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}">
                                    {action.description}
                                </p>
                            </div>
                        </li>
                    {/each}
                </ol>
                {#if hiddenActionCount > 0}
                    <button
                        type="button"
                        class="mt-4 inline-flex items-center gap-2 !border-0 !bg-transparent text-sm font-medium {linkClass}"
                        aria-expanded={actionsExpanded}
                        aria-controls={actionsId}
                        on:click={toggleActions}
                        data-testid="bill-detail-actions-toggle"
                    >
                        {actionsExpanded ? t.Show_Fewer_Actions() : t.Show_All_Actions(String(detail.actions.length))}
                        <svg class="h-4 w-4 transition-transform {actionsExpanded ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                {/if}
            </section>
        {/if}

        {#if detail.committees.length > 0}
            <section class="mb-8" data-testid="bill-detail-committees">
                <h2 class={headingClass}>{t.Detail_Committees()}</h2>
                <ul class="list-disc space-y-1 pl-5 text-sm text-gray-700">
                    {#each detail.committees as committee}
                        <li>{committee}</li>
                    {/each}
                </ul>
            </section>
        {/if}

        {#if detail.relatedProposals.length > 0}
            <section class="mb-8 scroll-mt-4" bind:this={relatedSection} data-testid="bill-detail-related">
                <h2 class={headingClass}>{t.Detail_Related_Bills()}</h2>
                <ul id={relatedId} class="m-0 list-none p-0 divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {#each visibleRelated as related}
                        <li class="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:gap-3">
                            <a href={billDetailRoute(related.url)} use:link class="shrink-0 text-sm font-semibold {linkClass}">
                                {related.identifierDisplay}
                            </a>
                            <span class="text-sm text-gray-700">{related.shortTitle}</span>
                        </li>
                    {/each}
                </ul>
                {#if hiddenRelatedCount > 0}
                    <button
                        type="button"
                        class="mt-4 inline-flex items-center gap-2 !border-0 !bg-transparent text-sm font-medium {linkClass}"
                        aria-expanded={relatedExpanded}
                        aria-controls={relatedId}
                        on:click={toggleRelated}
                        data-testid="bill-detail-related-toggle"
                    >
                        {relatedExpanded
                            ? t.Show_Fewer_Related_Bills()
                            : t.Show_All_Related_Bills(String(detail.relatedProposals.length))}
                        <svg class="h-4 w-4 transition-transform {relatedExpanded ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                {/if}
            </section>
        {/if}

        {#if detail.urls.length > 0}
            <section data-testid="bill-detail-links">
                <h2 class={headingClass}>{t.Detail_More_Information()}</h2>
                <ul class="m-0 flex list-none flex-wrap gap-2 p-0">
                    {#each detail.urls as url}
                        <li>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                class="inline-flex items-center gap-2 rounded-lg !border !border-solid !border-[#1b75d0] !px-4 !py-2 text-sm font-medium !text-[#1b75d0] !no-underline transition-colors hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#2563eb)] focus-visible:ring-offset-2"
                            >
                                {urlLabel(url)}
                                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                <span class="sr-only">({t.Opens_In_New_Tab()})</span>
                            </a>
                        </li>
                    {/each}
                </ul>
            </section>
        {/if}
    </div>
</article>
